import { computed, ref, watch } from 'vue';
import { defineStore, type StoreGeneric, storeToRefs } from 'pinia';
import {
  CreateContentCollectionRequest,
  Cursor,
  ShareableContentCollection,
  Identity,
  ContentCollection,
} from '@ilteducation/content-collections-types';
import useContentCollectionsStore from './content-collections';
import {
  create as createApi,
  destroy as destroyApi,
  getOwnedCollections,
  reorderCollections,
  update as updateApi,
} from './api';
import { CollectionPatch, NewContentCollection } from './types';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMyProfiles } from '@ilteducation/profile';

const useMyCollectionsStore = defineStore('my-collections', () => {
  const collectionIds = ref<string[]>([]);
  const isLoading = ref(false);
  const cursor = ref<Cursor>();
  const positions = ref<Record<string, number>>({});

  const { collectionsByIds, put: putInContentStore } = useContentCollectionsStore();
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  const { current: currentProfile } = useMyProfiles();

  const hasMore = computed(() => cursor.value !== null);
  const collections = computed<ShareableContentCollection[]>(() => {
    const loaded = collectionsByIds(collectionIds) as ShareableContentCollection[];
    const newestFirst = (a: ShareableContentCollection, b: ShareableContentCollection) => b.legacyId - a.legacyId;
    const unsaved = loaded.filter(({ id }) => positions.value[id] === undefined).sort(newestFirst);
    const saved = loaded
      .filter(({ id }) => positions.value[id] !== undefined)
      .sort((a, b) => positions.value[a.id]! - positions.value[b.id]! || newestFirst(a, b));
    return [...unsaved, ...saved];
  });

  const identity = computed<Identity>(() => ({
    userId: user.value.uid,
    profileId: currentProfile.value?.id,
  }));

  watch([user, currentProfile], () => {
    // reset on identity change
    collectionIds.value = [];
    cursor.value = undefined;
    positions.value = {};
  });

  const load = async (limit: number, context: string): Promise<void> => {
    if (!hasMore.value || isLoading.value) {
      return;
    }

    isLoading.value = true;
    try {
      const { data, cursor: newCursor } = await getOwnedCollections(
        context,
        currentProfile.value?.id,
        cursor.value as string | undefined,
        limit,
        undefined,
      );

      putInContentStore(data);

      const existingIds = new Set(collectionIds.value);
      data.forEach((collection) => {
        if (!existingIds.has(collection.id)) {
          collectionIds.value.push(collection.id);
        }
        // `position` is newer than the published content-collections-types package.
        const { position } = collection as { position?: number };
        if (position !== undefined) {
          positions.value[collection.id] = position;
        }
      });

      cursor.value = newCursor;
    } catch (error) {
      console.error('Failed to load my collections:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const create = async (params: NewContentCollection): Promise<ContentCollection> => {
    isLoading.value = true;
    try {
      const updatedParams: CreateContentCollectionRequest = {
        ...params,
        owners: [identity.value],
        creator: identity.value,
      };
      const collection = await createApi(updatedParams);

      putInContentStore([collection]);
      collectionIds.value.push(collection.id);

      return collection;
    } catch (error) {
      console.error('Failed to create my collection:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const update = async (id: string, params: CollectionPatch): Promise<void> => {
    isLoading.value = true;
    try {
      const collection = await updateApi(id, params, identity.value.profileId);
      putInContentStore([{ ...collection, id }]);
    } catch (error) {
      console.error(`Failed to update my collection ${id}:`, error);
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (collectionId: string): Promise<void> => {
    isLoading.value = true;
    try {
      await destroyApi(collectionId, identity.value.profileId);

      const indexToRemove = collectionIds.value.findIndex((id) => id === collectionId);
      if (indexToRemove !== -1) {
        collectionIds.value.splice(indexToRemove, 1);
      }
      // TODO: remove from content-collections store
      // TODO: trigger removal from views?
    } catch (error) {
      console.error(`Failed to remove my collection ${collectionId}:`, error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveOrder = async (context: string, movedCollectionId: string, afterCollectionId?: string): Promise<void> => {
    const previous = positions.value;
    const ids = collections.value.map(({ id }) => id).filter((id) => id !== movedCollectionId);
    ids.splice(afterCollectionId ? ids.indexOf(afterCollectionId) + 1 : 0, 0, movedCollectionId);
    positions.value = Object.fromEntries(ids.map((collectionId, position) => [collectionId, position]));
    try {
      await reorderCollections(context, movedCollectionId, afterCollectionId, identity.value.profileId);
    } catch (error) {
      console.error('Failed to save my collections order:', error);
      positions.value = previous;
      throw error;
    }
  };

  const $reset = () => {
    collectionIds.value = [];
    cursor.value = undefined;
    positions.value = {};
  };

  const reset = () => {
    $reset();
  };

  return {
    collections,
    isLoading: computed(() => isLoading.value),
    hasMore,
    reset,
    $reset,
    load,
    create,
    update,
    remove,
    saveOrder,
  };
});

export default useMyCollectionsStore;
